import { useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';

import { useQuery } from '@hooks/reactQuery';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { getTypes } from '@api/assets';
import { getDownloadURL } from '@api/common';

import { IAssetTypeSubtypeResponse, TAssetTypeSubTypeItem } from '@interfaces/assets/asset';

import { refetchOptions } from '@constants/common';
import { ASSET_TYPE_ICON_URL, ASSETS_TYPES } from '@constants/queryKeys';

interface IUseQueryAssetTypesParams {
	isAssetTypesSubtypesWithIconUrl?: boolean;
}

export const useQueryAssetTypes = (params?: IUseQueryAssetTypesParams) => {
	const [assetTypesSubtypesWithIconUrl, setAssetTypesSubtypesWithIconUrl] = useState<TAssetTypeSubTypeItem[]>();

	const { isAssetTypesSubtypesWithIconUrl = true } = params || {};

	const {
		accessPermission: {
			assetOwnerApprovalUnit,
			assetOwnerAttributeUpdater,
			assetOwnerContact,
			assetOwnerDefaultContact,
			assetOwnerManageRequests,
			assetOwnerSignatory,
			assetOwnerViewer,
			assetOwnerOrganizationAdministrator,
			networkOperatorAssetEnquirer,
			networkOperatorAssetViewer,
			assetConsumerAssetEnquirer,
			assetConsumerAssetViewer,
		},
	} = useAppSelector(auth);

	const isAssetsAvailable = (
		assetOwnerApprovalUnit ||
		assetOwnerAttributeUpdater ||
		assetOwnerContact ||
		assetOwnerDefaultContact ||
		assetOwnerManageRequests ||
		assetOwnerSignatory ||
		assetOwnerViewer ||
		assetOwnerOrganizationAdministrator ||
		networkOperatorAssetEnquirer ||
		networkOperatorAssetViewer ||
		assetConsumerAssetEnquirer ||
		assetConsumerAssetViewer
	);

	const {
		data: assetTypesSubtypes,
		isInitialLoading,
	} = useQuery<Array<IAssetTypeSubtypeResponse>, Array<TAssetTypeSubTypeItem>>({
		queryKey: [ASSETS_TYPES],
		queryFn: getTypes,
		select: (assetTypesSubtypes: Array<IAssetTypeSubtypeResponse>) => {
			return assetTypesSubtypes.map(option => ({
				...option,
				value: option.type.id,
				label: option.type.assetTypeName,
			}));
		},
		enabled: [isAssetsAvailable],
		...refetchOptions,
	});

	const urlQueries = useQueries({
		queries: assetTypesSubtypes?.map((item) => {
			return ({
				queryKey: [ASSET_TYPE_ICON_URL, item.type.id],
				queryFn: () => getDownloadURL(item.type.icon?.id as string),
				enabled: !!item.type.icon?.id && isAssetTypesSubtypesWithIconUrl,
				staleTime: Infinity,
				refetchOnMount: false,
				refetchOnWindowFocus: false,
				refetchOnReconnect: false,
			});
		}) || [],
	});

	useEffect(() => {
		const isAllQueriesSuccess = !isAssetTypesSubtypesWithIconUrl
			|| urlQueries.every((query) => !!query.data?.downloadUrl);

		if (assetTypesSubtypes?.length && isAllQueriesSuccess && !assetTypesSubtypesWithIconUrl) {
			const assetTypesSubtypesWithIcons = assetTypesSubtypes?.map((item, index) => {
				if (!item.type.icon?.id) return item;

				return {
					...item,
					type: {
						...item.type,
						icon: {
							...item.type.icon,
							downloadUrl: urlQueries[index].data?.downloadUrl,
						},
					},
				};
			});

			setAssetTypesSubtypesWithIconUrl(assetTypesSubtypesWithIcons);
		}
	}, [
		assetTypesSubtypes,
		assetTypesSubtypesWithIconUrl,
		isAssetTypesSubtypesWithIconUrl,
		urlQueries,
	]);

	return {
		assetTypesSubtypes: assetTypesSubtypesWithIconUrl || [],
		isLoadingAssetTypes: isInitialLoading,
	};
};
